import { skipToken } from "@reduxjs/toolkit/query";
import { hasGptRealtime2VoiceStack } from "@repo/app-types/entities/company/livekitAgentFlags";
import type { ConfigurationOperation } from "@repo/app-types/entities/configurationAgent";
import type {
  KnowledgeBase,
  KnowledgeBaseAttachment,
} from "@repo/app-types/entities/knowledgeBase";
import { buildKnowledgeBaseDocumentName } from "@repo/app-types/entities/knowledgeRetrievalTool";
import {
  buildGradingOutcome,
  resolveOutcomeValue,
} from "@repo/app-types/entities/outcome";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import {
  TeammateAgentBranch,
  type AdminApiPlaybookTestSpec,
} from "@repo/app-types/entities/teammateAgent";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import {
  isKnowledgeBaseDocumentOperation,
  isTestCaseOperation,
} from "@repo/app-types/helpers/configurationAgentParser";
import { useCallback, useState } from "react";
import {
  useAllKnowledgeBasesQuery,
  useGetKnowledgeBaseAttachmentsQuery,
} from "../../api/entities/knowledgeBase.js";
import {
  useAllPlaybookTestSpecsQuery,
  useCreatePlaybookTestSpecMutation,
  useDeletePlaybookTestSpecMutation,
  useUpdatePlaybookTestSpecMutation,
} from "../../api/entities/playbookTestSpec.js";
import { useDeleteTeammateCopilotMessagesMutation } from "../../api/entities/teammate.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import { useAutosaveDraft } from "../../hooks/useAutosaveDraft.js";
import {
  usePlaybookEditor,
  type UsePlaybookEditorResult,
} from "../../hooks/usePlaybookEditor.js";
import {
  usePlaybookSaveStatus,
  type PlaybookSaveStatus,
} from "../../hooks/usePlaybookSaveStatus.js";
import {
  useTeammateAgentConfigEditor,
  type UseTeammateAgentConfigEditorResult,
} from "../../hooks/useTeammateAgentConfigEditor.js";
import { useWaitForAgentPrompt } from "../../hooks/useWaitForAgentPrompt.js";
import { selectCompany } from "../company/companySlice.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";
import type { PublishableChanges } from "./publishButton.js";

export interface EditingAttachment {
  knowledgeBaseId: string;
  attachmentId: string;
}

/**
 * Everything the agent-config surfaces (legacy Configuration tab and the static
 * agent layout) need to render the shared toolbar, AI Assistant sidebar,
 * simulation slideover, and the editable config blocks. Centralizing this here
 * keeps the legacy tab and the static tabs behaviorally identical.
 */
export interface TeammateAgentConfigController {
  teammateAgentId: TeammateId;
  playbookId: PlaybookId | null;
  isStaticAgent: boolean;
  isConfigurationLocked: boolean;
  /** The agent's assigned number, used as outbound caller ID and for inbound. */
  phoneNumber: string | null;

  agentConfig: UseTeammateAgentConfigEditorResult;
  playbook: UsePlaybookEditorResult;

  /** All knowledge bases available to attach. */
  allKnowledgeBases: readonly KnowledgeBase[] | undefined;
  /** Attachments for the currently-selected knowledge base. */
  knowledgeBaseAttachments: readonly KnowledgeBaseAttachment[] | undefined;
  /** Test specs for the agent's playbook (assistant lookups). */
  testSpecs: AdminApiPlaybookTestSpec[] | undefined;

  // Aggregate save/publish state
  isSaving: boolean;
  hasUnpublishedChanges: boolean;
  saveStatus: PlaybookSaveStatus;
  isPublishing: boolean;
  isDiscarding: boolean;
  isActionInProgress: boolean;
  publishableChanges: PublishableChanges;
  onPublish: () => Promise<void>;
  onDiscardChanges: () => Promise<void>;

  // Simulation ("Try this agent")
  isSimulationSlideoverOpen: boolean;
  setIsSimulationSlideoverOpen: (open: boolean) => void;
  simulationKey: number;
  isPreparingAgent: boolean;
  shouldUseLiveToolCalls: boolean;
  setShouldUseLiveToolCalls: (value: boolean) => void;
  isDeletingMessages: boolean;
  handleOpenSimulation: () => Promise<void>;
  handleResetSimulation: () => Promise<void>;

  // Knowledge base attachment editor slideover
  editingAttachment: EditingAttachment | null;
  setEditingAttachment: (editing: EditingAttachment | null) => void;
  pendingPatch: { find: string; replace: string } | null;
  setPendingPatch: (patch: { find: string; replace: string } | null) => void;

  handleDataExtractionFieldsChange: (fieldIds: string[]) => void;
  handleApplyOperation: (operation: ConfigurationOperation) => Promise<void>;

  /**
   * Authoritatively persist any pending agent-config draft edits right now.
   * Used before triggering an action that reads the server-side draft (e.g. the
   * instruction-section checks), mirroring the "Try this agent" flush.
   */
  flushAgentConfigSave: () => Promise<boolean>;
}

/**
 * Owns the full lifecycle of teammate agent config editing: the agent-config
 * and playbook editors, autosave, publish/discard, the "Try this agent"
 * simulation, the KB attachment editor, and the AI assistant's apply-operation
 * handler. Call this once per agent-config surface (the legacy Configuration
 * tab, or the static agent layout that provides it to its tabs via context).
 */
export function useTeammateAgentConfigController(
  teammateAgentId: TeammateId,
): TeammateAgentConfigController {
  const dispatch = useAppDispatch();

  const { data: teammateAgent } = useGetTeammateAgentQuery({
    id: teammateAgentId,
    branch: TeammateAgentBranch.Live,
  });
  const teammateAgentConfig = teammateAgent?.teammate_agent_config;

  const company = useAppSelector(selectCompany);
  const playbookId = teammateAgentConfig?.playbook_id ?? null;
  const isOutboundVoiceAgentsEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.OutboundVoiceAgents,
  );

  const { data: allKnowledgeBases } = useAllKnowledgeBasesQuery();

  const [isSimulationSlideoverOpen, setIsSimulationSlideoverOpen] =
    useState(false);
  const [simulationKey, setSimulationKey] = useState(0);
  const [isPreparingAgent, setIsPreparingAgent] = useState(false);
  const [shouldUseLiveToolCalls, setShouldUseLiveToolCalls] = useState(false);

  const [editingAttachment, setEditingAttachment] =
    useState<EditingAttachment | null>(null);
  const [pendingPatch, setPendingPatch] = useState<{
    find: string;
    replace: string;
  } | null>(null);

  const [deleteTeammateCopilotMessages, { isLoading: isDeletingMessages }] =
    useDeleteTeammateCopilotMessagesMutation();

  // Test case mutations and query
  const [createPlaybookTestSpec] = useCreatePlaybookTestSpecMutation();
  const [updatePlaybookTestSpec] = useUpdatePlaybookTestSpecMutation();
  const [deletePlaybookTestSpec] = useDeletePlaybookTestSpecMutation();
  const { data: allTestSpecs } = useAllPlaybookTestSpecsQuery(
    playbookId ? { playbookId } : skipToken,
  );

  const { waitForPromptReady } = useWaitForAgentPrompt({ teammateAgentId });

  const agentConfig = useTeammateAgentConfigEditor(teammateAgentId);
  const playbook = usePlaybookEditor(playbookId);

  const isStaticAgent = agentConfig.isStatic;

  // The GPT Realtime 2 voice stack is configured via its meta prompt in HQ, so
  // its presence locks admin-side config for legacy agents. Static agents are
  // authored in admin (their prompt is built from the sections/settings edited
  // here), so the lock doesn't apply to them.
  const isConfigurationLocked =
    !isStaticAgent &&
    hasGptRealtime2VoiceStack(company?.settings.livekit_agent_flags ?? []);

  const {
    isSaving: isAgentConfigSaving,
    hasUnsavedChanges: agentConfigHasUnsavedChanges,
    hasUnpublishedChanges: agentConfigHasUnpublishedChanges,
    isValid: agentConfigIsValid,
    unpublishedChanges: agentConfigUnpublishedChanges,
    lastSavedAt: agentConfigLastSavedAt,
    onDiscardChanges: onAgentConfigDiscardChanges,
    onPublishChanges: onAgentConfigPublish,
    onSaveDraft: onAgentConfigSaveDraft,
  } = agentConfig;

  const {
    data: playbookData,
    liveData: playbookLiveData,
    isSaving: isPlaybookSaving,
    hasUnsavedChanges: playbookHasUnsavedChanges,
    hasUnpublishedChanges: playbookHasUnpublishedChanges,
    unpublishedChanges: playbookUnpublishedChanges,
    lastSavedAt: playbookLastSavedAt,
    onChange: onPlaybookChange,
    onDiscardChanges: onPlaybookDiscardChanges,
    onPublishChanges: onPlaybookPublish,
    onSaveDraft: onPlaybookSaveDraft,
  } = playbook;

  // Fetch attachments for the selected KB so AI operations can look up documents by name
  const selectedKnowledgeBaseId = playbookData?.knowledgeBaseIds?.[0];
  const { data: knowledgeBaseAttachments } =
    useGetKnowledgeBaseAttachmentsQuery(selectedKnowledgeBaseId ?? skipToken);

  // Track which action is in progress (for button loading states)
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const isActionInProgress = isPublishing || isDiscarding;

  // Set up autosave for agent config
  // Only autosave when data is valid to avoid 400 errors from the server
  const { flushPendingSave: flushAgentConfigSave } = useAutosaveDraft({
    hasChanges: agentConfigHasUnsavedChanges,
    canAutosave:
      teammateAgentId !== null &&
      !isConfigurationLocked &&
      !isAgentConfigSaving &&
      !isActionInProgress &&
      agentConfigIsValid,
    onSave: onAgentConfigSaveDraft,
  });

  // Set up autosave for playbook (only for existing playbooks)
  const { flushPendingSave: flushPlaybookSave } = useAutosaveDraft({
    hasChanges: playbookHasUnsavedChanges,
    canAutosave:
      playbookId !== null &&
      !isConfigurationLocked &&
      !isPlaybookSaving &&
      !isActionInProgress,
    onSave: onPlaybookSaveDraft,
  });

  // Compute combined save status for display
  const isSaving = isAgentConfigSaving || isPlaybookSaving;
  const hasUnpublishedChanges =
    agentConfigHasUnpublishedChanges || playbookHasUnpublishedChanges;

  // Use the most recent lastSavedAt between agent config and playbook
  const lastSavedAt = (() => {
    if (!agentConfigLastSavedAt && !playbookLastSavedAt) return null;
    if (!agentConfigLastSavedAt) return playbookLastSavedAt;
    if (!playbookLastSavedAt) return agentConfigLastSavedAt;
    return new Date(agentConfigLastSavedAt) > new Date(playbookLastSavedAt)
      ? agentConfigLastSavedAt
      : playbookLastSavedAt;
  })();

  const saveStatus = usePlaybookSaveStatus({
    hasUnpublishedChanges,
    isSaving,
    lastSavedAt,
  });

  const onPublish = useCallback(async () => {
    if (isConfigurationLocked) {
      showNotification(dispatch, {
        id: `agent-configuration-locked-${Date.now().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Configuration is locked",
        secondaryMessage:
          "This agent is currently locked and cannot be edited.",
      });
      return;
    }

    setIsPublishing(true);
    try {
      // Publish agent config if there are unpublished changes
      if (agentConfigHasUnpublishedChanges) {
        await onAgentConfigPublish();
      }

      // Publish playbook if there are unpublished changes
      if (playbookHasUnpublishedChanges) {
        await onPlaybookPublish();
      }

      showNotification(dispatch, {
        id: `agent-update-${new Date().getTime().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Changes published",
        secondaryMessage: "Your changes have been published successfully.",
      });
    } catch (error) {
      console.error("Failed to publish changes:", error);
      showNotification(dispatch, {
        id: `agent-update-error-${new Date().getTime().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to publish changes",
        secondaryMessage: "Please try again.",
      });
    } finally {
      setIsPublishing(false);
    }
  }, [
    agentConfigHasUnpublishedChanges,
    onAgentConfigPublish,
    playbookHasUnpublishedChanges,
    onPlaybookPublish,
    dispatch,
    isConfigurationLocked,
  ]);

  const onDiscardChanges = useCallback(async () => {
    if (isConfigurationLocked) {
      return;
    }

    setIsDiscarding(true);
    try {
      // Discard agent config changes
      await onAgentConfigDiscardChanges();

      // Revert playbook draft to live via hook
      await onPlaybookDiscardChanges();
    } finally {
      setIsDiscarding(false);
    }
  }, [
    onAgentConfigDiscardChanges,
    onPlaybookDiscardChanges,
    isConfigurationLocked,
  ]);

  const handleResetSimulation = useCallback(async () => {
    if (!teammateAgent) return;

    try {
      await deleteTeammateCopilotMessages({
        teammateId: teammateAgent.id,
        chatDetails: {
          type: "teammate_agent",
          teammate_agent_id: teammateAgentId,
        },
      }).unwrap();

      // Reset the component by changing the key
      setSimulationKey((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to reset simulation:", error);
    }
  }, [teammateAgent, deleteTeammateCopilotMessages, teammateAgentId]);

  const handleOpenSimulation = useCallback(async () => {
    setIsPreparingAgent(true);

    try {
      // Flush any pending edits to draft first so the simulation uses the latest.
      // Going through the autosave flush (rather than the raw savers) clears the
      // debounce timer and chains after any in-flight autosave via `inFlightRef`,
      // so we don't fork the draft revision with a concurrent write. This also
      // marks the prompt as stale and enqueues background regeneration.
      const agentConfigSaved = await flushAgentConfigSave();
      const playbookSaved = await flushPlaybookSave();
      if (!agentConfigSaved || !playbookSaved) {
        throw new Error("Failed to save draft before simulation");
      }

      // Wait for the prompt to be regenerated
      await waitForPromptReady();

      await handleResetSimulation();
      setIsSimulationSlideoverOpen(true);
    } catch (error) {
      console.error("Failed to prepare agent:", error);
      showNotification(dispatch, {
        id: `regenerate-prompt-error-${new Date().getTime().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to prepare agent",
        secondaryMessage: "Please try again.",
      });
    } finally {
      setIsPreparingAgent(false);
    }
  }, [
    flushAgentConfigSave,
    flushPlaybookSave,
    waitForPromptReady,
    dispatch,
    handleResetSimulation,
  ]);

  // Handler for data extraction field changes
  const handleDataExtractionFieldsChange = useCallback(
    (fieldIds: string[]) => {
      onPlaybookChange({ gradingDataExtractionFieldIds: fieldIds });
    },
    [onPlaybookChange],
  );

  // Handler for applying AI assistant operations
  const handleApplyOperation = useCallback(
    async (operation: ConfigurationOperation): Promise<void> => {
      if (isConfigurationLocked) {
        throw new Error("Configuration is locked in HQ.");
      }

      if (isTestCaseOperation(operation)) {
        if (!playbookId) {
          throw new Error(
            "Cannot apply test case operation: playbook not found",
          );
        }

        // Build expected outcomes from the operation's expectedOutcome string
        // First resolve the outcome value (which may be a label like "Escalated"
        // or a value like "support_resolution:escalated") to the proper value format
        const outcomeDefinition = playbookData?.outcome;
        const resolvedOutcomeValue =
          outcomeDefinition && operation.type !== "remove-test-case"
            ? resolveOutcomeValue(outcomeDefinition, operation.expectedOutcome)
            : undefined;
        const expectedOutcomes =
          outcomeDefinition && resolvedOutcomeValue
            ? buildGradingOutcome(outcomeDefinition, resolvedOutcomeValue)
            : undefined;

        switch (operation.type) {
          case "add-test-case":
            await createPlaybookTestSpec({
              playbookId,
              payload: {
                name: operation.name,
                description: operation.description,
                expected: {
                  nodes: {},
                  outcomes: expectedOutcomes,
                },
              },
            }).unwrap();
            break;
          case "update-test-case":
            await updatePlaybookTestSpec({
              playbookId,
              testSpecId: operation.id,
              payload: {
                name: operation.name ?? "",
                description: operation.description ?? "",
                expected: {
                  nodes: {},
                  outcomes: expectedOutcomes,
                },
              },
            }).unwrap();
            break;
          case "remove-test-case":
            await deletePlaybookTestSpec({
              playbookId,
              testSpecId: operation.id,
            }).unwrap();
            break;
        }
      } else if (isKnowledgeBaseDocumentOperation(operation)) {
        if (!selectedKnowledgeBaseId || !knowledgeBaseAttachments) {
          throw new Error(
            "Cannot apply KB document operation: no knowledge base selected",
          );
        }

        const attachment = knowledgeBaseAttachments.find(
          (a) =>
            buildKnowledgeBaseDocumentName({ title: a.title }) ===
            operation.documentName,
        );

        if (!attachment) {
          throw new Error(
            `Document "${operation.documentName}" not found in knowledge base`,
          );
        }

        setEditingAttachment({
          knowledgeBaseId: selectedKnowledgeBaseId,
          attachmentId: attachment.id,
        });
        setPendingPatch(operation.patch);
      }
    },
    [
      playbookData?.outcome,
      playbookId,
      createPlaybookTestSpec,
      updatePlaybookTestSpec,
      deletePlaybookTestSpec,
      selectedKnowledgeBaseId,
      knowledgeBaseAttachments,
      isConfigurationLocked,
    ],
  );

  // For the publish dropdown, show granular unpublished changes
  const publishableChanges: PublishableChanges = {
    identityNameChanged: agentConfigUnpublishedChanges.identityNameChanged,
    identityRoleChanged: agentConfigUnpublishedChanges.identityRoleChanged,
    identityAdditionalContextChanged:
      agentConfigUnpublishedChanges.identityAdditionalContextChanged,
    toolIdsChanged: agentConfigUnpublishedChanges.toolIdsChanged,
    instructionSectionsChanged:
      agentConfigUnpublishedChanges.instructionSectionsChanged,
    // Gated: a company de-flagged mid-edit can still have staged settings, and
    // listing a change they have no surface to inspect or fix is worse than
    // saying nothing.
    callingSettingsChanged:
      isOutboundVoiceAgentsEnabled &&
      agentConfigUnpublishedChanges.settingsChanged,
    dataExtractionChanged: playbookUnpublishedChanges.dataExtractionChanged,
    playbookChanged: playbookUnpublishedChanges.treeChanged,
    knowledgeBasesChanged: playbookUnpublishedChanges.knowledgeBasesChanged,
    outcomeChanged: playbookUnpublishedChanges.outcomeChanged,
    summaryTemplateChanged: playbookUnpublishedChanges.summaryTemplateChanged,
    playbookInitialTemplate: playbookLiveData?.tree,
    playbookCurrentTemplate: playbookData?.tree,
  };

  return {
    teammateAgentId,
    playbookId,
    isStaticAgent,
    isConfigurationLocked,
    phoneNumber: teammateAgentConfig?.phone_number ?? null,
    agentConfig,
    playbook,
    allKnowledgeBases,
    knowledgeBaseAttachments,
    testSpecs: allTestSpecs,
    isSaving,
    hasUnpublishedChanges,
    saveStatus,
    isPublishing,
    isDiscarding,
    isActionInProgress,
    publishableChanges,
    onPublish,
    onDiscardChanges,
    isSimulationSlideoverOpen,
    setIsSimulationSlideoverOpen,
    simulationKey,
    isPreparingAgent,
    shouldUseLiveToolCalls,
    setShouldUseLiveToolCalls,
    isDeletingMessages,
    handleOpenSimulation,
    handleResetSimulation,
    editingAttachment,
    setEditingAttachment,
    pendingPatch,
    setPendingPatch,
    handleDataExtractionFieldsChange,
    handleApplyOperation,
    flushAgentConfigSave,
  };
}
